import React, { useCallback, useEffect, useRef } from 'react'

import { useRemoteStrings } from '@acorns/web-utils'

import { useBlueprintBindings } from '../utils/blueprint-bindings-context'
import type { GroveBlueprintComponentProps } from '../utils/grove-blueprint-component-props'
import { RenderGroveBlueprintNode } from '../utils/render-grove-blueprint-node'
import { resolveModifiers } from '../utils/resolve-modifiers'
import { useScrollViewPosition } from '../utils/use-scroll-view-position'

export function GroveBlueprintScrollView({
  node,
}: GroveBlueprintComponentProps) {
  const { getRemoteString } = useRemoteStrings()
  const { bindingValues } = useBlueprintBindings()

  const scrollViewPositionRef = useScrollViewPosition()
  const elementRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    if (scrollViewPositionRef && elementRef.current) {
      scrollViewPositionRef.current = elementRef.current.scrollTop
    }
  }, [scrollViewPositionRef])

  useEffect(
    function restoreScrollPositionOnVisibilityChange() {
      const element = elementRef.current

      if (!element || !scrollViewPositionRef) {
        return
      }

      const observer = new MutationObserver(() => {
        if (element.offsetParent !== null) {
          element.scrollTop = scrollViewPositionRef.current
        }
      })

      const wrapper = element.parentElement

      if (wrapper) {
        observer.observe(wrapper, {
          attributes: true,
          attributeFilter: ['class'],
        })
      }

      return function cleanup() {
        observer.disconnect()
      }
    },
    [scrollViewPositionRef],
  )

  /* v8 ignore if -- @preserve */
  if (node.type !== 'ScrollView') {
    return null
  }

  const { props, modifiers, children } = node

  const {
    className: modifierClasses,
    style: modifierStyles,
    attributes,
  } = resolveModifiers(modifiers, bindingValues, (key: string) =>
    getRemoteString({ key }),
  )

  const scrollClass =
    props.axis === 'horizontal' ? 'overflow-x-auto' : 'overflow-y-auto'

  return (
    <div
      className={`${scrollClass} flex-1 min-h-0 ${modifierClasses}`.trim()}
      onScroll={handleScroll}
      ref={elementRef}
      style={modifierStyles}
      {...attributes}
    >
      {children?.map((child, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <RenderGroveBlueprintNode key={index} node={child} />
      ))}
    </div>
  )
}
